import { A } from "@solidjs/router";
import { LogoNoText } from "@storytell/core";
import { TbBrandLinkedin, TbCheck, TbLink } from "solid-icons/tb";
import { createSignal, onCleanup, Show } from "solid-js";
import { PostGrid } from "~/components/marketing/BlogCards";
import { DownloadButton } from "~/components/marketing/CTAButtons";
import { normalizeLabels } from "~/routes/(static)/blog/blogLabels";
import { type Post, visiblePosts } from "~/routes/(static)/blog/posts";

const sectionLabelClass = "text-xs font-bold uppercase tracking-wider text-muted-text";

// Posts never name an individual (BRAND); the byline is the team.
export function Byline() {
  return (
    <div class="flex items-center gap-3">
      <span class="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-panel">
        <LogoNoText class="h-3 w-auto text-accent" />
      </span>
      <span class="text-sm font-semibold text-primary-text">The Chief team</span>
    </div>
  );
}

export function ShareLinks(props: { url: string; title: string }) {
  const [copied, setCopied] = createSignal(false);
  let resetTimer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(resetTimer));
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(props.url);
    } catch {
      return;
    }
    setCopied(true);
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => setCopied(false), 2000);
  };
  const buttonClass =
    "inline-flex size-11 items-center justify-center rounded-lg border border-primary-border bg-panel text-secondary-text transition-colors hover:text-primary-text";
  return (
    <div class="flex flex-col gap-2.5">
      <span class={sectionLabelClass}>Share</span>
      <div class="flex gap-2">
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(props.url)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn"
          class={buttonClass}
        >
          <TbBrandLinkedin class="size-4" aria-hidden="true" />
        </a>
        <a
          href={`https://x.com/intent/post?url=${encodeURIComponent(props.url)}&text=${encodeURIComponent(props.title)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X"
          class={buttonClass}
        >
          {/* solid-icons predates the X mark. */}
          <svg viewBox="0 0 24 24" fill="currentColor" class="size-3.5" aria-hidden="true">
            <path d="M17.75 3h3.07l-6.7 7.66L22 21h-6.17l-4.83-6.32L5.47 21H2.4l7.17-8.2L2 3h6.33l4.37 5.77L17.75 3Zm-1.08 16.17h1.7L7.4 4.74H5.58l11.09 14.43Z" />
          </svg>
        </a>
        <button
          type="button"
          onClick={() => void copy()}
          aria-label={copied() ? "Link copied" : "Copy link"}
          class={buttonClass}
        >
          <Show when={copied()} fallback={<TbLink class="size-4" aria-hidden="true" />}>
            <TbCheck class="size-4" aria-hidden="true" />
          </Show>
        </button>
      </div>
    </div>
  );
}

export function PostCta() {
  return (
    <div class="flex flex-col gap-3 rounded-2xl border border-primary-border bg-panel p-5 [&>a]:justify-center [&>button]:justify-center">
      <LogoNoText class="h-4 w-auto self-start text-accent" />
      <span class="text-[17px] leading-snug font-bold text-primary-text">Say it. Chief captures it.</span>
      <span class="text-sm leading-normal text-secondary-text">
        Every conversation becomes a record you can act on.
      </span>
      <DownloadButton from="blog-post" size="md" />
      <A
        href="/book-chief-demo"
        class="rounded-lg border border-primary-border px-3.5 py-2 text-center text-sm font-medium text-primary-text no-underline transition-colors hover:border-brand"
      >
        Book a demo
      </A>
    </div>
  );
}

/** Up to three posts: those sharing the post's cluster first, then those sharing a label. */
export function RelatedPosts(props: { post: Post }) {
  const related = () => {
    const others = visiblePosts.filter((other) => other.slug !== props.post.slug);
    const labels = normalizeLabels(props.post.labels);
    const sameCluster = (other: Post) => !!props.post.cluster && other.cluster === props.post.cluster;
    const sameLabel = (other: Post) => normalizeLabels(other.labels).some((label) => labels.includes(label));
    return [...others.filter(sameCluster), ...others.filter((other) => !sameCluster(other) && sameLabel(other))].slice(
      0,
      3
    );
  };
  return (
    <Show when={related().length > 0}>
      <section aria-labelledby="keep-reading" class="mt-16 border-t border-primary-border pt-10">
        <h2 id="keep-reading" class="mb-6 text-2xl font-bold tracking-tight text-primary-text">
          Keep reading
        </h2>
        <PostGrid posts={related()} />
      </section>
    </Show>
  );
}
